import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import { Gauge, Cog, Users, Fuel, Star, Search, Crown } from "lucide-react";
import { useBooking } from "./BookingProvider";

const Spec = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-1.5 text-white/60">
    <Icon className="w-4 h-4 text-primary" />
    <span className="text-xs">{label}</span>
  </div>
);

const VehicleCard = ({ v, i, onBook }) => (
  <motion.article
    data-testid={`vehicle-card-${v.id}`}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
    className={`group rounded-2xl overflow-hidden bg-[#141414] border transition-colors duration-300 flex flex-col ${
      v.premium ? "border-primary/40 hover:border-primary" : "border-white/10 hover:border-primary/50"
    }`}
  >
    <div className="relative h-52 overflow-hidden">
      <img
        src={v.image}
        alt={v.name}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#141414] to-transparent" />
      <Badge className="absolute top-4 left-4 bg-black/70 text-primary border border-primary/30 backdrop-blur">
        {v.type} · {v.category}
      </Badge>
      {v.premium && (
        <div className="absolute bottom-4 left-4 flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-black">
          <Crown className="w-3.5 h-3.5" />
          <span className="text-[11px] font-bold uppercase tracking-wide">Premium</span>
        </div>
      )}
      <div className="absolute top-4 right-4 flex items-center gap-1 rounded-full bg-black/70 px-2.5 py-1 backdrop-blur">
        <Star className="w-3.5 h-3.5 text-primary fill-primary" />
        <span className="text-xs font-semibold">{v.rating}</span>
      </div>
    </div>

    <div className="p-6 flex flex-col flex-1">
      <h3 className="text-xl font-display font-bold">{v.name}</h3>

      <div className="mt-4 grid grid-cols-2 gap-y-2.5 gap-x-3">
        <Spec icon={Cog} label={v.engine} />
        <Spec icon={Gauge} label={v.top_speed} />
        <Spec icon={Fuel} label={v.mileage} />
        <Spec icon={Users} label={`${v.seats} seats`} />
      </div>

      <div className="mt-6 grid grid-cols-3 gap-2 text-center">
        {[
          { k: "Daily", val: v.daily },
          { k: "Weekly", val: v.weekly },
          { k: "Monthly", val: v.monthly },
        ].map((p) => (
          <div key={p.k} className="rounded-lg bg-black/40 border border-white/5 py-2">
            <div className="text-lg font-bold gold-text">₹{p.val.toLocaleString("en-IN")}</div>
            <div className="text-[10px] uppercase tracking-wider text-white/40">{p.k}</div>
          </div>
        ))}
      </div>

      <Button
        data-testid={`book-vehicle-${v.id}`}
        onClick={() => onBook(v)}
        className="mt-6 w-full rounded-full bg-primary text-black hover:bg-primary/90 font-semibold"
      >
        Book Now
      </Button>
    </div>
  </motion.article>
);

const Group = ({ id, icon: Icon, title, subtitle, items, onBook }) => {
  if (items.length === 0) return null;
  return (
    <div className="mb-16" data-testid={`fleet-group-${id}`}>
      <div className="flex items-center gap-3 mb-8">
        <div className="grid place-items-center w-10 h-10 rounded-xl bg-primary/15 text-primary">
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h3 className="text-2xl font-display font-bold">{title}</h3>
          <p className="text-sm text-white/50">{subtitle}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {items.map((v, i) => (
          <VehicleCard key={v.id} v={v} i={i} onBook={onBook} />
        ))}
      </div>
    </div>
  );
};

export const Fleet = () => {
  const { vehicles, loading, openBooking } = useBooking();
  const [type, setType] = useState("all");
  const [price, setPrice] = useState("all");
  const [availability, setAvailability] = useState("all");

  const filtered = useMemo(() => {
    return vehicles.filter((v) => {
      if (type !== "all" && v.type !== type) return false;
      if (availability === "available" && !v.available) return false;
      if (price === "under500" && v.daily >= 500) return false;
      if (price === "500to1000" && (v.daily < 500 || v.daily > 1000)) return false;
      if (price === "over1000" && v.daily <= 1000) return false;
      return true;
    });
  }, [vehicles, type, price, availability]);

  const premium = filtered.filter((v) => v.premium);
  const standard = filtered.filter((v) => !v.premium);

  return (
    <section id="fleet" className="relative py-20 sm:py-28 bg-[#0A0A0A]">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-2xl mb-10">
          <span className="text-primary font-semibold text-sm uppercase tracking-widest">Our Fleet</span>
          <h2 className="mt-3 text-3xl sm:text-5xl font-display font-bold tracking-tight">
            Pick Your Perfect Ride
          </h2>
          <p className="mt-4 text-white/70 text-base">
            Looking for the best bike rental in Pune? From nimble city scooters like the Honda Activa
            to highway-ready Royal Enfields, rent a bike or scooter on affordable daily, weekly and
            monthly plans — filter by type, price and availability.
          </p>
        </div>

        {/* Search / Filter bar */}
        <div
          data-testid="fleet-filters"
          className="flex flex-col md:flex-row gap-4 p-4 sm:p-5 rounded-2xl bg-[#141414] border border-white/10 mb-12"
        >
          <div className="flex items-center gap-2 flex-1 text-white/50 px-2">
            <Search className="w-5 h-5 text-primary" />
            <span className="text-sm">Filter vehicles</span>
          </div>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="filter-type" className="md:w-44 bg-[#0A0A0A] border-white/15 text-white">
              <SelectValue placeholder="Vehicle Type" />
            </SelectTrigger>
            <SelectContent className="bg-[#141414] border-white/15 text-white">
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="Scooter">Scooters</SelectItem>
              <SelectItem value="Motorcycle">Motorcycles</SelectItem>
            </SelectContent>
          </Select>
          <Select value={price} onValueChange={setPrice}>
            <SelectTrigger data-testid="filter-price" className="md:w-44 bg-[#0A0A0A] border-white/15 text-white">
              <SelectValue placeholder="Price Range" />
            </SelectTrigger>
            <SelectContent className="bg-[#141414] border-white/15 text-white">
              <SelectItem value="all">Any Price</SelectItem>
              <SelectItem value="under500">Under ₹500/day</SelectItem>
              <SelectItem value="500to1000">₹500 – ₹1000/day</SelectItem>
              <SelectItem value="over1000">₹1000+/day</SelectItem>
            </SelectContent>
          </Select>
          <Select value={availability} onValueChange={setAvailability}>
            <SelectTrigger data-testid="filter-availability" className="md:w-44 bg-[#0A0A0A] border-white/15 text-white">
              <SelectValue placeholder="Availability" />
            </SelectTrigger>
            <SelectContent className="bg-[#141414] border-white/15 text-white">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="available">Available Now</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <p className="text-white/50 text-center py-20" data-testid="fleet-loading">Loading fleet…</p>
        ) : filtered.length === 0 ? (
          <p className="text-white/50 text-center py-20" data-testid="fleet-empty">
            No vehicles match your filters. Try adjusting them.
          </p>
        ) : (
          <>
            <Group
              id="premium"
              icon={Crown}
              title="Premium Collection"
              subtitle="Our finest bikes & scooters, handpicked for the best ride"
              items={premium}
              onBook={openBooking}
            />
            <Group
              id="standard"
              icon={Star}
              title="Bikes & Scooters"
              subtitle="Reliable, affordable rides for every journey"
              items={standard}
              onBook={openBooking}
            />
          </>
        )}
      </div>
    </section>
  );
};
